import {
  FilePreflightError,
  preflightRasterBytes,
  type SupportedRasterFormat,
} from '@/shared/image/filePreflight'

export const PHYSICAL_SIZE_LIMITS = {
  maxFileBytes: 50 * 1024 * 1024,
  maxImagePixels: 36_000_000,
  maxLongestSide: 32_768,
} as const

export type DensityState =
  | 'found'
  | 'not_recorded'
  | 'aspect_ratio_only'
  | 'asymmetric'
  | 'conflicting_sources'
  | 'unsupported_metadata_source'

export type DensityUnit = 'ppi' | 'ppcm' | 'ppm' | 'unitless' | 'invalid'
export type DensitySourceKind = 'jfif' | 'exif' | 'png_phys' | 'png_exif' | 'webp_exif' | 'xmp'

export interface DensitySource {
  kind: DensitySourceKind
  unit: DensityUnit
  rawX: number | null
  rawY: number | null
  xPpi: number | null
  yPpi: number | null
}

export interface PhysicalPhotoInspection {
  format: SupportedRasterFormat
  width: number
  height: number
  density: {
    state: DensityState
    sources: DensitySource[]
    xPpi: number | null
    yPpi: number | null
  }
}

export interface PhysicalDimensions {
  inches: { width: number; height: number }
  centimeters: { width: number; height: number }
  millimeters: { width: number; height: number }
}

const MAX_CONTAINER_ITEMS = 4_096
const PPI_TOLERANCE = 0.01

function ascii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.subarray(offset, offset + length))
}

function u16be(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] ?? 0) << 8) | (bytes[offset + 1] ?? 0)
}

function u32be(bytes: Uint8Array, offset: number): number {
  return ((bytes[offset] ?? 0) * 0x1000000)
    + ((bytes[offset + 1] ?? 0) << 16)
    + ((bytes[offset + 2] ?? 0) << 8)
    + (bytes[offset + 3] ?? 0)
}

function u32le(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0)
    + ((bytes[offset + 1] ?? 0) << 8)
    + ((bytes[offset + 2] ?? 0) << 16)
    + ((bytes[offset + 3] ?? 0) * 0x1000000)
}

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff
  for (const byte of bytes) {
    crc ^= byte
    for (let bit = 0; bit < 8; bit += 1) crc = (crc >>> 1) ^ (0xedb88320 & -(crc & 1))
  }
  return (crc ^ 0xffffffff) >>> 0
}


function invalidSource(kind: DensitySourceKind): DensitySource {
  return { kind, unit: 'invalid', rawX: null, rawY: null, xPpi: null, yPpi: null }
}

function densitySource(
  kind: DensitySourceKind,
  unit: DensityUnit,
  rawX: number,
  rawY: number,
): DensitySource {
  if (!Number.isFinite(rawX) || !Number.isFinite(rawY) || rawX <= 0 || rawY <= 0) {
    return invalidSource(kind)
  }
  const factor = unit === 'ppcm' ? 2.54 : unit === 'ppm' ? 0.0254 : unit === 'ppi' ? 1 : null
  return {
    kind,
    unit,
    rawX,
    rawY,
    xPpi: factor === null ? null : rawX * factor,
    yPpi: factor === null ? null : rawY * factor,
  }
}

function parseExifTiff(bytes: Uint8Array, kind: 'exif' | 'png_exif' | 'webp_exif'): DensitySource | null {
  if (bytes.length < 8) return invalidSource(kind)
  const order = ascii(bytes, 0, 2)
  const little = order === 'II'
  if (!little && order !== 'MM') return invalidSource(kind)
  const read16 = (offset: number) => little
    ? (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8)
    : u16be(bytes, offset)
  const read32 = (offset: number) => little ? u32le(bytes, offset) : u32be(bytes, offset)
  if (read16(2) !== 42) return invalidSource(kind)
  const ifdOffset = read32(4)
  if (!Number.isSafeInteger(ifdOffset) || ifdOffset < 8 || ifdOffset + 2 > bytes.length) {
    return invalidSource(kind)
  }
  const count = read16(ifdOffset)
  if (count > MAX_CONTAINER_ITEMS || ifdOffset + 2 + count * 12 + 4 > bytes.length) {
    return invalidSource(kind)
  }
  let x: number | null = null
  let y: number | null = null
  let unit: number | null = null
  const seen = new Set<number>()
  const rational = (entry: number): number | null => {
    const offset = read32(entry + 8)
    if (offset + 8 > bytes.length) return null
    const numerator = read32(offset)
    const denominator = read32(offset + 4)
    return denominator === 0 ? null : numerator / denominator
  }
  for (let index = 0; index < count; index += 1) {
    const entry = ifdOffset + 2 + index * 12
    const tag = read16(entry)
    if (tag !== 0x011a && tag !== 0x011b && tag !== 0x0128) continue
    if (seen.has(tag)) return invalidSource(kind)
    seen.add(tag)
    const type = read16(entry + 2)
    const valueCount = read32(entry + 4)
    if (tag === 0x0128) {
      if (type !== 3 || valueCount !== 1) return invalidSource(kind)
      unit = read16(entry + 8)
    } else {
      if (type !== 5 || valueCount !== 1) return invalidSource(kind)
      const value = rational(entry)
      if (value === null) return invalidSource(kind)
      if (tag === 0x011a) x = value
      else y = value
    }
  }
  if (x === null && y === null && unit === null) return null
  if (x === null || y === null) return invalidSource(kind)
  if (unit === 2 || unit === null) return densitySource(kind, 'ppi', x, y)
  if (unit === 3) return densitySource(kind, 'ppcm', x, y)
  if (unit === 1) return densitySource(kind, 'unitless', x, y)
  return invalidSource(kind)
}

function parseJpegSources(bytes: Uint8Array): DensitySource[] {
  const sources: DensitySource[] = []
  let offset = 2
  let segments = 0
  while (offset < bytes.length - 2) {
    if (bytes[offset] !== 0xff || ++segments > MAX_CONTAINER_ITEMS) break
    while (bytes[offset] === 0xff) offset += 1
    const marker = bytes[offset] ?? 0
    offset += 1
    if (marker === 0xda || marker === 0xd9) break
    if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) continue
    const length = u16be(bytes, offset)
    if (length < 2 || offset + length > bytes.length) break
    const payload = offset + 2
    const payloadLength = length - 2
    if (marker === 0xe0 && payloadLength >= 14 && ascii(bytes, payload, 5) === 'JFIF\0') {
      const units = bytes[payload + 7] ?? 0
      const x = u16be(bytes, payload + 8)
      const y = u16be(bytes, payload + 10)
      const unit: DensityUnit | null = units === 0
        ? 'unitless'
        : units === 1 ? 'ppi' : units === 2 ? 'ppcm' : null
      sources.push(unit === null ? invalidSource('jfif') : densitySource('jfif', unit, x, y))
    } else if (marker === 0xe1 && payloadLength >= 6 && ascii(bytes, payload, 6) === 'Exif\0\0') {
      const source = parseExifTiff(bytes.subarray(payload + 6, payload + payloadLength), 'exif')
      if (source) sources.push(source)
    } else if (marker === 0xe1 && payloadLength > 0) {
      const text = ascii(bytes, payload, Math.min(payloadLength, 65_536))
      if (/XResolution|YResolution|ResolutionUnit/.test(text)) sources.push(invalidSource('xmp'))
    }
    offset += length
  }
  return sources
}

function parsePngSources(bytes: Uint8Array): DensitySource[] {
  const sources: DensitySource[] = []
  let offset = 8
  let chunks = 0
  while (offset + 12 <= bytes.length && ++chunks <= MAX_CONTAINER_ITEMS) {
    const length = u32be(bytes, offset)
    const type = ascii(bytes, offset + 4, 4)
    const data = offset + 8
    const crcOffset = data + length
    const next = crcOffset + 4
    if (!Number.isSafeInteger(next) || next > bytes.length) break
    if (u32be(bytes, crcOffset) !== crc32(bytes.subarray(offset + 4, crcOffset))) {
      throw new FilePreflightError('invalid_container')
    }
    if (type === 'pHYs') {
      if (length !== 9) sources.push(invalidSource('png_phys'))
      else {
        const unit = bytes[data + 8]
        sources.push(unit === 0 || unit === 1
          ? densitySource('png_phys', unit === 1 ? 'ppm' : 'unitless', u32be(bytes, data), u32be(bytes, data + 4))
          : invalidSource('png_phys'))
      }
    } else if (type === 'eXIf') {
      const source = parseExifTiff(bytes.subarray(data, data + length), 'png_exif')
      if (source) sources.push(source.unit === 'invalid'
        ? source
        : { ...source, unit: 'invalid', xPpi: null, yPpi: null })
    } else if ((type === 'iTXt' || type === 'tEXt' || type === 'zTXt') && length > 0) {
      const text = ascii(bytes, data, Math.min(length, 65_536))
      if (/XResolution|YResolution|ResolutionUnit/.test(text)) sources.push(invalidSource('xmp'))
    }
    offset = next
    if (type === 'IEND') break
  }
  return sources
}

function parseWebpSources(bytes: Uint8Array): DensitySource[] {
  const sources: DensitySource[] = []
  let offset = 12
  let chunks = 0
  while (offset + 8 <= bytes.length && ++chunks <= MAX_CONTAINER_ITEMS) {
    const type = ascii(bytes, offset, 4)
    const length = u32le(bytes, offset + 4)
    const data = offset + 8
    const next = data + length + (length % 2)
    if (!Number.isSafeInteger(next) || next > bytes.length) break
    if (type === 'EXIF') {
      const start = ascii(bytes, data, Math.min(6, length)) === 'Exif\0\0' ? data + 6 : data
      const source = parseExifTiff(bytes.subarray(start, data + length), 'webp_exif')
      if (source) sources.push(source)
    } else if (type === 'XMP ') {
      const text = ascii(bytes, data, Math.min(length, 65_536))
      if (/XResolution|YResolution|ResolutionUnit/.test(text)) sources.push(invalidSource('xmp'))
    }
    offset = next
  }
  return sources
}

function close(a: number, b: number): boolean {
  return Math.abs(a - b) <= PPI_TOLERANCE
}


function sameRawDensity(a: DensitySource, b: DensitySource): boolean {
  return a.unit === b.unit && a.rawX === b.rawX && a.rawY === b.rawY
}

function duplicateDensityState(sources: DensitySource[]): DensityState | null {
  const firstByKind = new Map<DensitySourceKind, DensitySource>()
  let hasSameValueDuplicate = false
  for (const source of sources) {
    const first = firstByKind.get(source.kind)
    if (!first) {
      firstByKind.set(source.kind, source)
      continue
    }
    if (!sameRawDensity(first, source)) return 'conflicting_sources'
    hasSameValueDuplicate = true
  }
  return hasSameValueDuplicate ? 'unsupported_metadata_source' : null
}

export function resolveDensity(sources: DensitySource[]): PhysicalPhotoInspection['density'] {
  if (sources.some((source) => source.unit === 'invalid')) {
    return { state: 'unsupported_metadata_source', sources, xPpi: null, yPpi: null }
  }
  const duplicateState = duplicateDensityState(sources)
  if (duplicateState) {
    return { state: duplicateState, sources, xPpi: null, yPpi: null }
  }
  const bearing = sources.filter((source) => source.xPpi !== null && source.yPpi !== null)
  if (bearing.length === 0) {
    return {
      state: sources.length === 0 ? 'not_recorded' : 'aspect_ratio_only',
      sources,
      xPpi: null,
      yPpi: null,
    }
  }
  const first = bearing[0]!
  const conflict = bearing.some((source) =>
    !close(source.xPpi!, first.xPpi!) || !close(source.yPpi!, first.yPpi!))
  if (conflict) return { state: 'conflicting_sources', sources, xPpi: null, yPpi: null }
  return {
    state: close(first.xPpi!, first.yPpi!) ? 'found' : 'asymmetric',
    sources,
    xPpi: first.xPpi,
    yPpi: first.yPpi,
  }
}

export function inspectPhysicalPhotoBytes(
  bytes: Uint8Array,
  format: SupportedRasterFormat,
): PhysicalPhotoInspection {
  const dimensions = preflightRasterBytes(bytes, format, PHYSICAL_SIZE_LIMITS)
  const sources = format === 'jpeg'
    ? parseJpegSources(bytes)
    : format === 'png'
      ? parsePngSources(bytes)
      : parseWebpSources(bytes)
  return { format, width: dimensions.width, height: dimensions.height, density: resolveDensity(sources) }
}

export function calculatePhysicalDimensions(
  width: number,
  height: number,
  xPpi: number,
  yPpi: number = xPpi,
): PhysicalDimensions | null {
  if (![width, height, xPpi, yPpi].every(Number.isFinite) || width <= 0 || height <= 0 || xPpi <= 0 || yPpi <= 0) {
    return null
  }
  const widthInches = width / xPpi
  const heightInches = height / yPpi
  const values = [widthInches, heightInches, widthInches * 2.54, heightInches * 2.54]
  if (!values.every(Number.isFinite)) return null
  return {
    inches: { width: widthInches, height: heightInches },
    centimeters: { width: widthInches * 2.54, height: heightInches * 2.54 },
    millimeters: { width: widthInches * 25.4, height: heightInches * 25.4 },
  }
}

export function detectPhysicalPhotoFormat(file: Pick<File, 'name' | 'type' | 'size'>): SupportedRasterFormat {
  if (file.size > PHYSICAL_SIZE_LIMITS.maxFileBytes) throw new FilePreflightError('file_too_large')
  const type = file.type.toLowerCase()
  const name = file.name.toLowerCase()
  if (type === 'image/jpeg' || name.endsWith('.jpg') || name.endsWith('.jpeg')) return 'jpeg'
  if (type === 'image/png' || name.endsWith('.png')) return 'png'
  if (type === 'image/webp' || name.endsWith('.webp')) return 'webp'
  throw new FilePreflightError('unsupported')
}
